import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { AddCircle, Delete, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Card, Divider, IconButton, LinearProgress, ListItem, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { getApiUrl } from '/src/api';
import { ConfirmModal } from '/src/components';
import { toastMessages } from '/src/lib/messages';
import { CRUDMode, Nullable, S3UploadResponse, URLString, UploadStatus } from '/src/lib/types';
import { toJSON } from '/src/utils';

export type DocumentsProps = {
  /**
   * CRUD operation to perform
   */
  mode: CRUDMode;
  /**
   * File URLs to display
   */
  documents: URLString[];
  /**
   * This component's index in a page's sidebar
   */
  sidebarIndex: number;
  /**
   * All of the enclosing page's sidebar item open/close statuses
   */
  sidebarItems: boolean[];
  /**
   * Set open/close status of page's sidebar items
   * @param items Open/close status of each item
   * @returns void
   */
  setSidebarItems: (items: boolean[]) => void;
  /**
   * Updates the files list
   * @param documents Array of URLs pointing to each document.
   * @returns void | Promise<void>
   */
  updateDocuments?: (documents: URLString[]) => void | Promise<void>;
  /**
   * Component title
   */
  title?: string;
  /**
   * Create button tooltip
   */
  tooltip?: string;
};

export const Documents: React.FC<DocumentsProps> = observer(
  ({
    mode,
    documents,
    updateDocuments,
    sidebarIndex,
    sidebarItems,
    setSidebarItems,
    title = 'Documents',
    tooltip = 'Add Document',
  }) => {
    /** Refs **/
    const xhr = useRef(new XMLHttpRequest());

    /** State **/
    const [indexToDelete, setIndexToDelete] = useState<number>();
    const [uploadStatus, setUploadStatus] = useState<UploadStatus>(UploadStatus.Ready);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [files, setFiles] = useState<URLString[]>([]);
    const [queue, setQueue] = useState<FileList>({} as FileList);
    const [showControls, setShowControls] = useState<boolean[]>([]);
    const [, setError] = useState(false);

    const fileInput = document.getElementById('fileInput') as HTMLInputElement | null;

    /** Methods **/

    /*** Returns the filename from a given URL ***/
    const getFileName = (url: URLString, keepExtension?: boolean) => {
      let fileName = url.slice(url.lastIndexOf('/') + 1);

      // If no value was passed, default to true
      if (!keepExtension) keepExtension = true;

      // Remove extension
      if (!keepExtension) fileName = fileName.slice(0, fileName.indexOf('.'));

      return fileName;
    };

    /*** Opens File Upload dialog ***/
    const openFileDialog = () => {
      if (uploadStatus !== UploadStatus.Uploading) fileInput?.click();
    };

    /*** Opens a file in another window ***/
    const openFile = (fileUrl: URLString) => {
      window.open(fileUrl, '_blank');
    };

    /*** High-level upload  ***/
    const upload = () => {
      if (!updateDocuments) return;

      // Gets signed request
      getSignedRequest((response) => {
        // Uploads the file to S3
        uploadFiles(queue[0], response.signed_request, response.url, (newFileUrl: URLString) => {
          setUploadStatus(UploadStatus.Done);

          // Updates the proper database entry with the file's URL
          updateDocuments([...files, newFileUrl]);

          // Show alert
          toast.success(toastMessages.uploadDocument.success);

          // Resets
          setUploadProgress(0);
          setUploadStatus(UploadStatus.Ready);

          // Open documents list item (if applicable)
          if (sidebarItems) setSidebarItems(sidebarItems.with(sidebarIndex, true));
        });
      });
    };

    /*** Get Signed Request ***/
    const getSignedRequest = (callback: (res: S3UploadResponse) => void) => {
      if (queue.length > 0) {
        const apiUrl = getApiUrl();
        const file = queue[0];
        xhr.current.open('GET', `${apiUrl}/s3request?file_name=${file.name}&file_type=${file.type}`);
        xhr.current.onreadystatechange = () => {
          if (xhr.current.readyState === 4) {
            if (xhr.current.status === 200) {
              const response = toJSON(xhr.current.response);
              if (response) callback(response);
            }
          }
        };
        xhr.current.send();
      } else {
        // To-Do: Turn this into a proper error
      }
    };

    /*** Upload Files to S3 ***/
    const uploadFiles = (file: any, signedRequest: any, url: string, callback: (url: string) => void) => {
      setUploadStatus(UploadStatus.Uploading);

      // Track upload progress
      xhr.current.upload.onprogress = (upload: ProgressEvent<EventTarget>) => {
        setUploadProgress((upload.loaded / upload.total) * 100);
      };

      // Open connection & set headers
      xhr.current.open('PUT', signedRequest);
      xhr.current.setRequestHeader('x-amz-acl', 'public-read');

      xhr.current.onload = () => {
        // Success callback
        if (xhr.current.status === 200) {
          setFiles([...files, url]);
          callback(url);
        }

        // Forbidden error callback
        else if (xhr.current.status === 403) {
          setUploadStatus(UploadStatus.Ready);
          setError(true);

          window.setTimeout(() => {
            setError(false);
          }, 2000);
        }
      };

      // General error callback
      xhr.current.onerror = () => {
        setUploadStatus(UploadStatus.Ready);
        setError(true);
      };

      // Engage
      xhr.current.send(file);
    };

    /*** Remove file ***/
    const removeFile = async () => {
      if (!updateDocuments) return;

      const fileIndex = indexToDelete;
      if (typeof fileIndex !== 'number' || !files[fileIndex]) return;

      try {
        const updatedFiles = files;
        updatedFiles.splice(fileIndex, 1);

        if (mode === CRUDMode.Edit) {
          // Push updates to the database
          await updateDocuments(updatedFiles);

          setFiles(updatedFiles);
          setIndexToDelete(undefined);

          if (updatedFiles.length < 1) setSidebarItems(sidebarItems.with(sidebarIndex, false));

          // Show alert
          toast.success(toastMessages.deleteDocument.success);
        }
      } catch (err) {
        console.error(err);
        toast.error(toastMessages.deleteDocument.error);
      }
    };

    const handleFileSelect = (files: Nullable<FileList>) => {
      if (files) {
        setQueue(files);
        setUploadStatus(UploadStatus.Queued);
      }
    };

    /** Effects **/

    useEffect(() => {
      if (documents) setFiles(documents);
    }, [documents]);

    useEffect(() => {
      if (queue.length && uploadStatus === UploadStatus.Queued) upload();
    }, [queue, uploadStatus]);

    /** Render **/
    return (
      <Box>
        <Card component="md-card" className="no-margin-bottom md-default-theme text-center">
          <ListItem className="toolbar-div pad-left">
            {mode !== CRUDMode.Read && (
              <Tooltip title={tooltip}>
                <IconButton
                  className="md-icon-button float-left"
                  onClick={() => openFileDialog()}
                  disabled={uploadStatus === UploadStatus.Uploading}
                >
                  <AddCircle style={{ color: 'white' }} className="md-icon material-icons" />
                </IconButton>
              </Tooltip>
            )}

            <Box sx={{ flex: 1 }}>{title}</Box>
            <Box sx={{ flex: 1 }}>
              <Box component="span" className="float-right">
                <IconButton
                  className="md-icon-button"
                  onClick={() => setSidebarItems(sidebarItems.with(sidebarIndex, !sidebarItems[sidebarIndex]))}
                  disabled={!files.length}
                >
                  <Tooltip title={sidebarItems[sidebarIndex] ? 'Show Less' : 'Show More'}>
                    {sidebarItems[sidebarIndex] ? (
                      <ExpandLess
                        style={{ color: !files.length ? undefined : 'white' }}
                        className="md-icon material-icons"
                      />
                    ) : (
                      <ExpandMore
                        style={{ color: !files.length ? undefined : 'white' }}
                        className="md-icon material-icons"
                      />
                    )}
                  </Tooltip>
                </IconButton>
              </Box>
            </Box>

            <input
              type="file"
              id="fileInput"
              onChange={(e) => handleFileSelect(e.target.files)}
              style={{ display: 'none' }}
            />
          </ListItem>

          {sidebarItems[sidebarIndex] && !!files.length && (
            <Box>
              {files.map((file, fileIndex) => (
                <Box key={fileIndex} bgcolor="white">
                  <ListItem
                    sx={{
                      paddingLeft: '16px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    onMouseOver={() => {
                      const controls = showControls;
                      controls[fileIndex] = true;
                      setShowControls(controls);
                    }}
                    onMouseOut={() => {
                      const controls = showControls;
                      controls[fileIndex] = false;
                      setShowControls(controls);
                    }}
                  >
                    <Box onClick={() => openFile(file)} sx={{ flex: 1 }} className="pointer">
                      {getFileName(file)}
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <IconButton className="md-icon-button md-warn" onClick={() => setIndexToDelete(fileIndex)}>
                      <Tooltip title="Delete File">
                        <Delete sx={{ fontSize: '18px' }} />
                      </Tooltip>
                    </IconButton>
                  </ListItem>

                  <Divider />
                </Box>
              ))}

              {uploadStatus === UploadStatus.Uploading && (
                <ListItem>
                  <LinearProgress
                    className="md-primary"
                    value={uploadProgress}
                    // md-mode="determinate"
                  />
                </ListItem>
              )}
            </Box>
          )}
        </Card>

        <ConfirmModal
          content="Are you sure you want to delete this Document?"
          label="Confirm Deletion"
          open={typeof indexToDelete === 'number'}
          setOpen={() => setIndexToDelete(undefined)}
          okLabel="Delete"
          onOk={() => removeFile()}
          cancelLabel="Cancel"
        />
      </Box>
    );
  }
);
